import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { useSocket } from '../../../hooks/useSocket';
import { useDataCache } from '../../../hooks/useDataCache';
import FormularioCard from './FormularioCard';
import FormularioDialog from './FormularioDialog';
import UsuariosDialog from './UsuariosDialog';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const FormularioGeneralManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    socket,
    isConnected,
    formularioData,
    joinFormularioUpdates,
    leaveFormularioUpdates,
    requestFormularioUsuarios,
    requestGET
  } = useSocket();

  const { getCached, setCached, invalidateCache } = useDataCache();

  const [formularios, setFormularios] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [selectedPreguntas, setSelectedPreguntas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUsuarioDialog, setOpenUsuarioDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [enviandoCredenciales, setEnviandoCredenciales] = useState({});
  const [isRefreshingUsuarios, setIsRefreshingUsuarios] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
  });

  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [usuariosAgregados, setUsuariosAgregados] = useState([]);
  const [idFormularioActual, setIdFormularioActual] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('TODAS');
  const [formulariosEspecificos, setFormulariosEspecificos] = useState({});

  const [editingUsuario, setEditingUsuario] = useState(null);
  const [fechasEdicion, setFechasEdicion] = useState({
    fecha_inicio: null,
    fecha_fin: null,
  });

  const token = localStorage.getItem('token');

  const showSnackbar = useCallback((message, severity = 'success') =>
    setSnackbar({ open: true, message, severity }), []);

  const resetForm = useCallback(() => {
    setFormData({ titulo: '', descripcion: '', fecha_inicio: '', fecha_fin: '' });
    setSelectedPreguntas([]);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Sin fecha';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Sin fecha';
      return date.toLocaleDateString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return 'Sin fecha';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return 'Sin fecha';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Sin fecha';
      const pad = (n) => n.toString().padStart(2, '0');
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    } catch {
      return 'Sin fecha';
    }
  }, []);

  const fetchFormularios = useCallback(async () => {
    try {
      setLoading(true);
      if (isConnected && typeof requestGET === 'function') {
        try {
          const res = await requestGET('/api/formularios-generales/list');
          if (res && res.status === 200) {
            setFormularios(res.data);
            return;
          }
        } catch (e) {
        }
      }

      const res = await axios.get(`${API_URL}/api/formularios-generales/list`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormularios(res.data);
    } catch {
      showSnackbar('Error al cargar formularios', 'error');
    } finally {
      setLoading(false);
    }
  }, [isConnected, requestGET, token, showSnackbar]);

  const fetchPreguntasCompletas = useCallback(async () => {
    try {
      const cached = getCached('preguntas_completas');
      if (cached) {
        setPreguntas(cached);
        return;
      }

      const preguntasRes = await axios.get(
        `${API_URL}/api/documentos/preguntas-con-parametros`,
        {
          params: { solo_activos: true },
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const preguntasConParametros = preguntasRes.data.map(p => ({
        ...p,
        activo: p.activo !== false,
        obligatorio: p.obligatorio !== false
      }));

      setPreguntas(preguntasConParametros);
      setCached('preguntas_completas', preguntasConParametros);

    } catch (error) {
      showSnackbar('Error al cargar preguntas y parámetros', 'error');
    }
  }, [token, showSnackbar, getCached, setCached]);

  const determinarAccionesUsuario = useCallback((usuario) => {
    const formUsuario = formulariosEspecificos[usuario.id_usuario];

    if (!formUsuario) {
      return {
        mostrarAgregar: true,
        mostrarReasignar: false,
        mostrarEditar: false,
        mostrarQuitar: false,
        mostrarEnviarCredenciales: false,
        esFinalizado: false,
        mostrarFechas: false
      };
    }

    const formularioActivoVisible = formUsuario.estado !== 'finalizado' && formUsuario.visible === 1;
    const formularioActivoOculto = formUsuario.estado !== 'finalizado' && formUsuario.visible === 0;
    const formularioFinalizado = formUsuario.estado === 'finalizado';

    if (formularioFinalizado) {
      return {
        mostrarAgregar: false,
        mostrarReasignar: true,
        mostrarEditar: false,
        mostrarQuitar: false,
        mostrarEnviarCredenciales: false,
        esFinalizado: true,
        mostrarFechas: false
      };
    }

    if (formularioActivoOculto) {
      return {
        mostrarAgregar: true,
        mostrarReasignar: false,
        mostrarEditar: false,
        mostrarQuitar: false,
        mostrarEnviarCredenciales: false,
        esFinalizado: false,
        mostrarFechas: false
      };
    }

    if (formularioActivoVisible) {
      return {
        mostrarAgregar: false,
        mostrarReasignar: false,
        mostrarEditar: true,
        mostrarQuitar: true,
        mostrarEnviarCredenciales: true,
        esFinalizado: false,
        mostrarFechas: true
      };
    }

    return {
      mostrarAgregar: true,
      mostrarReasignar: false,
      mostrarEditar: false,
      mostrarQuitar: false,
      mostrarEnviarCredenciales: false,
      esFinalizado: false,
      mostrarFechas: false
    };
  }, [formulariosEspecificos]);

  const openCreate = useCallback(() => {
    resetForm();
    setIsEditing(false);
    setEditId(null);
    setOpenDialog(true);
  }, [resetForm]);

  const openEdit = useCallback(async (form) => {
    setIsEditing(true);
    setEditId(form.id_formulario_general);
    setFormData({
      titulo: form.titulo,
      descripcion: form.descripcion || '',
      fecha_inicio: form.fecha_inicio || '',
      fecha_fin: form.fecha_fin || '',
    });

    try {
      const res = await axios.get(
        `${API_URL}/api/formularios-generales/${form.id_formulario_general}/preguntas`,
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPreguntas(res.data.map((p) => p.id_pregunta));
    } catch {
      setSelectedPreguntas([]);
    }

    setOpenDialog(true);
  }, [token]);

  const handleSave = useCallback(async () => {
    const calcularSumaPuntajesPorTipo = (seccion, tipoPersona) => {
      return selectedPreguntas.reduce((sum, preguntaId) => {
        const pregunta = preguntas.find(p => p.id_pregunta === preguntaId);
        if (pregunta && pregunta.tipo === 'documento' && pregunta.seccion === seccion && pregunta.tipo_persona === tipoPersona) {
          return sum + (parseFloat(pregunta.puntaje_maximo) || 0);
        }
        return sum;
      }, 0);
    };

    const sumaSeccion5_5Natural = calcularSumaPuntajesPorTipo('5_5', 'natural');
    const sumaSeccion5_5Juridica = calcularSumaPuntajesPorTipo('5_5', 'juridica');

    if (sumaSeccion5_5Natural !== 100 || sumaSeccion5_5Juridica !== 100) {
      let mensajeError = 'Las secciones 5_5 deben sumar exactamente 100 puntos cada una:';
      if (sumaSeccion5_5Natural !== 100) {
        mensajeError += ` Natural: ${sumaSeccion5_5Natural}/100`;
      }
      if (sumaSeccion5_5Juridica !== 100) {
        mensajeError += ` Jurídica: ${sumaSeccion5_5Juridica}/100`;
      }
      showSnackbar(mensajeError, 'error');
      return;
    }

    setSaving(true);
    try {
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing
        ? `${API_URL}/api/formularios-generales/${editId}`
        : `${API_URL}/api/formularios-generales/add`;

      const body = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        fecha_inicio: formData.fecha_inicio || null,
        fecha_fin: formData.fecha_fin || null,
      };

      const res = await axios({ method, url, data: body, withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      const saved = res.data;

      if (isEditing) {
        await axios.delete(`${API_URL}/api/formularios-generales/${editId}/preguntas`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      for (const preguntaId of selectedPreguntas) {
        await axios.post(
          `${API_URL}/api/formularios-generales/${saved.id_formulario_general}/preguntas`,
          { id_pregunta: preguntaId, orden: 0, obligatorio: true },
          { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
        );
      }

      showSnackbar(isEditing ? 'Formulario actualizado' : 'Formulario creado', 'success');
      setOpenDialog(false);
      fetchFormularios();
      resetForm();
    } catch {
      showSnackbar('Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  }, [isEditing, editId, formData, selectedPreguntas, preguntas, token, showSnackbar, fetchFormularios, resetForm]);

  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/api/formularios-generales/${deleteId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchFormularios();
      showSnackbar(`Formulario eliminado.`, 'info');
      setShowDeleteConfirm(false);
      setDeleteId(null);
    } catch (error) {
      showSnackbar('Error al eliminar formulario', 'error');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteId, token, fetchFormularios, showSnackbar]);

  const handleToggleStatus = useCallback(async (formulario) => {
    const newStatus = formulario.estado === 'activo' ? 'inactivo' : 'activo';
    try {
      await axios.put(
        `${API_URL}/api/formularios-generales/${formulario.id_formulario_general}/status`,
        { estado: newStatus },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );

      showSnackbar(`Formulario ${newStatus}.`, 'success');
      fetchFormularios();
    } catch {
      showSnackbar('Error al cambiar estado', 'error');
    }
  }, [token, fetchFormularios, showSnackbar]);

  const abrirModalUsuarios = useCallback(async (idFormulario) => {
    setIdFormularioActual(idFormulario);
    setOpenUsuarioDialog(true);
    setFormulariosEspecificos({});

    try {
      let todosLosUsuarios = getCached('usuarios_list');

      if (!todosLosUsuarios) {
        if (isConnected && typeof requestGET === 'function') {
          try {
            const res = await requestGET('/api/usuarios');
            if (res && res.status === 200) {
              todosLosUsuarios = Array.isArray(res.data) ? res.data :
                (res.data.usuarios || []);
            }
          } catch (e) {
          }
        }

        if (!todosLosUsuarios || todosLosUsuarios.length === 0) {
          const primeraPagina = await axios.get(`${API_URL}/api/usuarios`, {
            params: { page: 1, per_page: 100 },
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
          });

          const responseData = primeraPagina.data;
          todosLosUsuarios = responseData.usuarios || [];

          const totalPages = responseData.pagination?.pages || 1;

          if (totalPages > 1) {
            const promises = [];
            for (let page = 2; page <= totalPages; page++) {
              promises.push(
                axios.get(`${API_URL}/api/usuarios`, {
                  params: { page, per_page: 100 },
                  withCredentials: true,
                  headers: { Authorization: `Bearer ${token}` }
                })
              );
            }

            const resultados = await Promise.all(promises);
            resultados.forEach(res => {
              if (res.data.usuarios) {
                todosLosUsuarios = [...todosLosUsuarios, ...res.data.usuarios];
              }
            });
          }
        }

        setCached('usuarios_list', todosLosUsuarios);
      }

      const usuariosFiltrados = todosLosUsuarios.filter(u => u.rol !== 'revisor');
      setUsuariosDisponibles(usuariosFiltrados);

      if (isConnected) {
        joinFormularioUpdates(idFormulario);
        requestFormularioUsuarios(idFormulario);
      } else {
        const usuariosAgregadosRes = await axios.get(
          `${API_URL}/api/formularios-generales/${idFormulario}/usuarios`,
          { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
        );
        setUsuariosAgregados(usuariosAgregadosRes.data);
      }

      const idsUsuarios = usuariosFiltrados.map(u => u.id_usuario);

      const estadosRes = await axios.post(
        `${API_URL}/api/formularios-generales/${idFormulario}/usuarios/estados-batch`,
        { ids_usuarios: idsUsuarios },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setFormulariosEspecificos(estadosRes.data);

    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      showSnackbar('Error al cargar proveedores', 'error');
    }
  }, [isConnected, requestGET, token, joinFormularioUpdates, requestFormularioUsuarios, showSnackbar, getCached, setCached]);

  const refreshUsuariosData = useCallback(async () => {
    if (!idFormularioActual) return;

    setIsRefreshingUsuarios(true);

    try {
      let todosLosUsuarios = [];

      if (isConnected && typeof requestGET === 'function') {
        try {
          const res = await requestGET('/api/usuarios');
          if (res && res.status === 200) {
            todosLosUsuarios = Array.isArray(res.data) ? res.data : (res.data.usuarios || []);
          }
        } catch (e) {
        }
      }

      if (!todosLosUsuarios || todosLosUsuarios.length === 0) {
        const primeraPagina = await axios.get(`${API_URL}/api/usuarios`, {
          params: { page: 1, per_page: 100 },
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        });

        const responseData = primeraPagina.data;
        todosLosUsuarios = responseData.usuarios || [];

        const totalPages = responseData.pagination?.pages || 1;

        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(
              axios.get(`${API_URL}/api/usuarios`, {
                params: { page, per_page: 100 },
                withCredentials: true,
                headers: { Authorization: `Bearer ${token}` }
              })
            );
          }

          const resultados = await Promise.all(promises);
          resultados.forEach(res => {
            if (res.data.usuarios) {
              todosLosUsuarios = [...todosLosUsuarios, ...res.data.usuarios];
            }
          });
        }
      }

      setCached('usuarios_list', todosLosUsuarios);

      const usuariosFiltrados = todosLosUsuarios.filter(u => u.rol !== 'revisor');
      setUsuariosDisponibles(usuariosFiltrados);

      const idsUsuarios = usuariosFiltrados.map(u => u.id_usuario);

      const estadosRes = await axios.post(
        `${API_URL}/api/formularios-generales/${idFormularioActual}/usuarios/estados-batch`,
        { ids_usuarios: idsUsuarios },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setFormulariosEspecificos(estadosRes.data);
      invalidateCache(`formulario_${idFormularioActual}_estados`);

      if (isConnected && socket) {
        requestFormularioUsuarios(idFormularioActual);
      }

      showSnackbar('Datos actualizados correctamente', 'success');

    } catch (error) {
      console.error('Error al refrescar datos:', error);
      showSnackbar('Error al actualizar los datos', 'error');
    } finally {
      setIsRefreshingUsuarios(false);
    }
  }, [idFormularioActual, token, isConnected, requestGET, socket, requestFormularioUsuarios, setCached, invalidateCache, showSnackbar]);

  const agregarUsuario = useCallback(async (idUsuario) => {
    try {
      const { data: estado } = await axios.get(
        `${API_URL}/api/formularios-generales/${idFormularioActual}/usuarios/${idUsuario}/estado`,
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const formGeneral = formularios.find(f => f.id_formulario_general === idFormularioActual);
      const fechaInicioDefault = formGeneral?.fecha_inicio || null;
      const fechaFinDefault = formGeneral?.fecha_fin || null;

      const formularioOculto = estado.formularios?.find(
        f => f.estado !== 'finalizado' && f.visible === 0
      );

      const formularioFinalizado = estado.formularios?.find(
        f => f.estado === 'finalizado'
      );

      let newFormId = null;
      let mensaje = '';
      let accion = '';

      if (formularioOculto) {
        await axios.put(
          `${API_URL}/api/formularios/${formularioOculto.id_formulario}/status`,
          { visible: true },
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        newFormId = formularioOculto.id_formulario;
        mensaje = 'Formulario reactivado';
        accion = 'reactivated';

      } else if (formularioFinalizado) {
        const reasRes = await axios.post(
          `${API_URL}/api/formularios-generales/${idFormularioActual}/reasignar/${idUsuario}`,
          {},
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        newFormId = reasRes.data.id_formulario;
        mensaje = 'Nuevo formulario asignado (reasignado)';
        accion = 'reassigned';

      } else {
        const addRes = await axios.post(
          `${API_URL}/api/formularios-generales/${idFormularioActual}/usuarios`,
          { id_usuario: idUsuario },
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        newFormId = addRes.data.id_formulario;
        mensaje = 'Usuario agregado al formulario';
        accion = 'added';
      }

      showSnackbar(mensaje, 'success');

      const estadoAnterior = formulariosEspecificos[idUsuario];

      const esReasignacion = accion === 'reassigned';

      const nuevoEstado = {
        id_formulario: newFormId,
        id_formulario_general: idFormularioActual,
        estado: 'activo',
        visible: 1,
        fecha_inicio: fechaInicioDefault,
        fecha_fin: fechaFinDefault,
        notificado_credenciales: false,
        notificado: esReasignacion
          ? false
          : estadoAnterior?.notificado || false
      };

      setFormulariosEspecificos(prev => ({
        ...prev,
        [idUsuario]: nuevoEstado
      }));

      setUsuariosAgregados(prev => {
        const existe = prev.some(u => u.id_usuario === idUsuario);
        if (existe) {
          return prev.map(u =>
            u.id_usuario === idUsuario
              ? {
                ...u,
                formulario: nuevoEstado
              }
              : u
          );
        }
        const usuarioObj = usuariosDisponibles.find(u => u.id_usuario === idUsuario);
        return [...prev, {
          ...usuarioObj,
          formulario: nuevoEstado
        }];
      });

      if (isConnected && socket) {
        socket.emit('formulario_usuario_update', {
          formulario_id: idFormularioActual,
          user_id: idUsuario,
          action: accion,
          formulario_info: nuevoEstado
        });
      }

      invalidateCache(`formulario_${idFormularioActual}_estados`);

    } catch (error) {
      console.error('Error al agregar usuario:', error);
      showSnackbar(
        error.response?.data?.detail || 'Error al agregar proveedor',
        'error'
      );
    }
  }, [formularios, usuariosDisponibles, idFormularioActual, token, isConnected, socket, showSnackbar, invalidateCache, formulariosEspecificos]);

  const eliminarUsuario = useCallback(async (idUsuario) => {
    try {
      if (!idFormularioActual) {
        showSnackbar('ID de formulario no válido', 'error');
        return;
      }

      const formUsuario = formulariosEspecificos[idUsuario];

      if (!formUsuario) {
        showSnackbar('Usuario no tiene formulario asignado', 'error');
        return;
      }

      await axios.put(
        `${API_URL}/api/formularios/${formUsuario.id_formulario}/status`,
        { visible: false },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const estadoActualizado = {
        ...formUsuario,
        visible: 0
      };

      setFormulariosEspecificos(prev => ({
        ...prev,
        [idUsuario]: estadoActualizado
      }));

      setUsuariosAgregados(prev =>
        prev.map(u =>
          u.id_usuario === idUsuario && u.formulario
            ? {
              ...u,
              formulario: estadoActualizado
            }
            : u
        )
      );

      if (isConnected && socket) {
        socket.emit('formulario_usuario_update', {
          formulario_id: idFormularioActual,
          user_id: idUsuario,
          action: 'removed',
          formulario_info: estadoActualizado
        });
      }

      invalidateCache(`formulario_${idFormularioActual}_estados`);

      showSnackbar('Acceso al formulario removido', 'info');

    } catch (error) {
      console.error('Error al quitar usuario:', error);
      showSnackbar(
        error.response?.data?.detail || 'Error al quitar usuario',
        'error'
      );
    }
  }, [idFormularioActual, formulariosEspecificos, token, isConnected, socket, showSnackbar, invalidateCache]);

  const enviarCredencialesIndividual = useCallback(async (usuario) => {
    try {
      setEnviandoCredenciales((prev) => ({ ...prev, [usuario.id_usuario]: true }));
      await axios.post(
        `${API_URL}/api/formularios-generales/${idFormularioActual}/usuarios/${usuario.id_usuario}/enviar-credenciales`,
        {},
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      showSnackbar('Error al enviar credenciales', 'error');
      return false;
    } finally {
      setEnviandoCredenciales((prev) => ({ ...prev, [usuario.id_usuario]: false }));
    }
  }, [idFormularioActual, token, showSnackbar]);

  const toggleEdicion = useCallback((usuario) => {
    const formUsuario = formulariosEspecificos[usuario.id_usuario];

    if (editingUsuario === usuario.id_usuario) {
      setEditingUsuario(null);
      setFechasEdicion({ fecha_inicio: null, fecha_fin: null });
    } else {
      let fechaInicioObj = null;
      let fechaFinObj = null;

      if (formUsuario?.fecha_inicio) {
        const fechaStr = formUsuario.fecha_inicio;
        if (typeof fechaStr === 'string') {
          const datePart = fechaStr.split('T')[0];
          const [year, month, day] = datePart.split('-');
          if (year && month && day) {
            fechaInicioObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
          }
        }
      }

      if (formUsuario?.fecha_fin) {
        const fechaStr = formUsuario.fecha_fin;
        if (typeof fechaStr === 'string') {
          const datePart = fechaStr.split('T')[0];
          const [year, month, day] = datePart.split('-');
          if (year && month && day) {
            fechaFinObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
          }
        }
      }

      setEditingUsuario(usuario.id_usuario);
      setFechasEdicion({
        fecha_inicio: fechaInicioObj,
        fecha_fin: fechaFinObj,
      });
    }
  }, [editingUsuario, formulariosEspecificos]);

  const guardarFechasEdicion = useCallback(async (idFormulario, usuario) => {
    const formId = idFormulario || (formulariosEspecificos[usuario.id_usuario] ? formulariosEspecificos[usuario.id_usuario].id_formulario : null);

    if (!formId) {
      showSnackbar('ID de formulario no válido', 'error');
      return;
    }

    try {
      let fechaInicioISO = null;
      let fechaFinISO = null;

      if (fechasEdicion.fecha_inicio) {
        const year = fechasEdicion.fecha_inicio.getFullYear();
        const month = (fechasEdicion.fecha_inicio.getMonth() + 1).toString().padStart(2, '0');
        const day = fechasEdicion.fecha_inicio.getDate().toString().padStart(2, '0');
        fechaInicioISO = `${year}-${month}-${day}`;
      }

      if (fechasEdicion.fecha_fin) {
        const year = fechasEdicion.fecha_fin.getFullYear();
        const month = (fechasEdicion.fecha_fin.getMonth() + 1).toString().padStart(2, '0');
        const day = fechasEdicion.fecha_fin.getDate().toString().padStart(2, '0');
        fechaFinISO = `${year}-${month}-${day}`;
      }

      const response = await axios.put(
        `${API_URL}/api/formularios-generales/${formId}/usuarios/${usuario.id_usuario}/fechas`,
        {
          fecha_inicio: fechaInicioISO,
          fecha_fin: fechaFinISO,
        },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSnackbar('Fechas actualizadas correctamente', 'success');
        setEditingUsuario(null);

        const updatedForm = { ...formulariosEspecificos[usuario.id_usuario] };
        updatedForm.fecha_inicio = fechaInicioISO;
        updatedForm.fecha_fin = fechaFinISO;

        setFormulariosEspecificos(prev => ({
          ...prev,
          [usuario.id_usuario]: updatedForm
        }));
      } else {
        showSnackbar('Error al actualizar las fechas', 'error');
      }
    } catch (error) {
      console.error('Error al guardar fechas:', error);
      showSnackbar('Error al actualizar las fechas', 'error');
    }
  }, [fechasEdicion, formulariosEspecificos, token, showSnackbar]);

  const handleCloseUsuarioDialog = useCallback(() => {
    if (idFormularioActual && isConnected) {
      leaveFormularioUpdates(idFormularioActual);
    }
    setOpenUsuarioDialog(false);
    setIdFormularioActual(null);
    setUsuariosAgregados([]);
    setFormulariosEspecificos({});
  }, [idFormularioActual, isConnected, leaveFormularioUpdates]);

  useEffect(() => {
    if (openUsuarioDialog && isConnected && formularioData[idFormularioActual]) {
      const specificData = {};
      formularioData[idFormularioActual].forEach(u => {
        if (u.formulario) {
          specificData[u.id_usuario] = {
            id_formulario: u.formulario.id_formulario,
            id_formulario_general: idFormularioActual,
            estado: u.formulario.estado,
            visible: u.formulario.visible,
            fecha_inicio: u.formulario.fecha_inicio || null,
            fecha_fin: u.formulario.fecha_fin || null,
            notificado: u.formulario.notificado,
            notificado_credenciales: u.formulario.notificado_credenciales
          };
        }
      });
      setFormulariosEspecificos(specificData);
      setUsuariosAgregados(formularioData[idFormularioActual]);
    }
  }, [formularioData, openUsuarioDialog, isConnected, idFormularioActual]);

  useEffect(() => {
    fetchFormularios();
    fetchPreguntasCompletas();
  }, [fetchFormularios, fetchPreguntasCompletas]);

  useEffect(() => {
    if (!isConnected || !socket) return;
    const handler = (payload) => {
      if (!payload || !payload.action) return;
      if (payload.action === 'created') {
        setFormularios(prev => [payload.data, ...prev]);
      } else if (payload.action === 'updated') {
        setFormularios(prev => prev.map(f => f.id_formulario_general === payload.data.id_formulario_general ? payload.data : f));
      } else if (payload.action === 'deleted') {
        setFormularios(prev => prev.filter(f => f.id_formulario_general !== payload.data.id_formulario_general));
      }
    };

    socket.on('formularios_updated', handler);
    return () => {
      if (socket) socket.off('formularios_updated', handler);
    };
  }, [isConnected, socket]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={isMobile ? 2 : 3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexDirection={isMobile ? "column" : "row"} gap={2}>
        <Typography variant="h4" style={{ color: '#005793', margin: 0 }} textAlign={isMobile ? "center" : "left"}>
          Gestión de Formularios
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate} fullWidth={isMobile}>
          Generar Formulario
        </Button>
      </Box>

      <Grid container spacing={2} direction="column">
        {formularios
          .slice()
          .sort((a, b) => {
            const fechaA = a.fecha_creacion ? new Date(a.fecha_creacion).getTime() : 0;
            const fechaB = b.fecha_creacion ? new Date(b.fecha_creacion).getTime() : 0;
            return fechaB - fechaA;
          })
          .map((form) => (
            <Grid item xs={12} key={form.id_formulario_general}>
              <FormularioCard
                form={form}
                isMobile={isMobile}
                isSmallMobile={isSmallMobile}
                openEdit={openEdit}
                abrirModalUsuarios={abrirModalUsuarios}
                handleDelete={() => handleDeleteClick(form.id_formulario_general)}
                handleToggleStatus={handleToggleStatus}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
              />
            </Grid>
          ))}
      </Grid>

      <FormularioDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        isEditing={isEditing}
        formData={formData}
        setFormData={setFormData}
        isMobile={isMobile}
        preguntas={preguntas}
        selectedPreguntas={selectedPreguntas}
        setSelectedPreguntas={setSelectedPreguntas}
        handleSave={handleSave}
        saving={saving}
      />

      <UsuariosDialog
        openUsuarioDialog={openUsuarioDialog}
        handleCloseUsuarioDialog={handleCloseUsuarioDialog}
        isMobile={isMobile}
        isConnected={isConnected}
        idFormularioActual={idFormularioActual}
        selectedFormulario={formularios.find(f => f.id_formulario_general === idFormularioActual)}
        usuariosDisponibles={usuariosDisponibles}
        formulariosEspecificos={formulariosEspecificos}
        setFormulariosEspecificos={setFormulariosEspecificos}
        enviandoCredenciales={enviandoCredenciales}
        editingUsuario={editingUsuario}
        fechasEdicion={fechasEdicion}
        setFechasEdicion={setFechasEdicion}
        agregarUsuario={agregarUsuario}
        eliminarUsuario={eliminarUsuario}
        enviarCredencialesIndividual={enviarCredencialesIndividual}
        toggleEdicion={toggleEdicion}
        guardarFechasEdicion={guardarFechasEdicion}
        formatDate={formatDate}
        determinarAccionesUsuario={determinarAccionesUsuario}
        onRefresh={refreshUsuariosData}
        isRefreshing={isRefreshingUsuarios}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeleteId(null);
        }}
        onConfirm={confirmDelete}
        title="¿Está seguro que desea eliminar este formulario?"
        message="Esta acción eliminará el formulario y todas sus respuestas asociadas. No se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={deleteLoading}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FormularioGeneralManager;